import {
  TRANSITION_ENQUIRE,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
  TRANSITION_JOB_DONE,
  TRANSITION_JOB_DONE_CUSTOMER,
  TRANSITION_JOB_DONE_PRO,
  TRANSITION_JOB_DONE_RELEASE_FUNDS,
  TRANSITION_RELEASE_FUNDS,
  TRANSITION_JOB_ACCEPTED,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_MAKE_CUSTOM_OFFER,
  TRANSITION_MAKE_CUSTOM_PRO_OFFER,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  TRANSITION_JOB_DONE_INVOICE,
  TRANSITION_ACCEPT_JOB_INVOICE,
  TRANSITION_JOB_DONE_ACCEPT_INVOICE,
} from './transaction';

/**
 * Helper utilities for process steps and transitions
 * This centralizes logic used by both ProcessStepsCard and ActivityFeed
 * to ensure consistent behavior across components
 */

/**
 * Get the timestamp for when a step started
 * @param {object} step - The step configuration
 * @param {string} status - The step status ('completed', 'current', 'pending')
 * @param {number} stepIndex - The step's index
 * @param {array} processSteps - All process steps
 * @param {array} transitions - Transaction transitions array
 * @returns {Date|null} - The timestamp when the step started
 */
export const getStepTimestamp = (step, status, stepIndex, processSteps, transitions) => {
  // Pending steps have no timestamp
  if (status === 'pending') return null;

  // First step starts when transaction is created (first transition)
  if (stepIndex === 0) {
    const firstTransition = transitions[0];
    return firstTransition?.createdAt || null;
  }

  // For all other steps, show when they started
  // This is the timestamp of the previous step's completion transition
  const previousStep = processSteps[stepIndex - 1];
  if (!previousStep || !previousStep.completedBy) return null;

  const matchingTransition = transitions.find(t => previousStep.completedBy.includes(t.transition));
  return matchingTransition?.createdAt || null;
};

/**
 * Determine if a transition should show an action needed state
 * @param {string} transition - The transition name
 * @param {string} lastTransition - The current last transition
 * @param {string} role - User role ('customer' or 'provider')
 * @returns {boolean} - Whether action is needed
 */
export const isActionNeeded = (transition, lastTransition, role) => {
  if (transition !== lastTransition) return false;

  const actionNeededMap = {
    // Customer needs to accept offer
    [TRANSITION_MAKE_CUSTOM_OFFER]: role === 'customer',
    [TRANSITION_MAKE_CUSTOM_PRO_OFFER]: role === 'customer',

    // Provider needs to mark work complete OR customer can release funds directly
    [TRANSITION_CONFIRM_PAYMENT]: true, // Both provider and customer have actions
    [TRANSITION_ACCEPT_CUSTOM_PRO_OFFER]: true, // Both provider and customer have actions

    // Customer needs to release funds
    [TRANSITION_JOB_DONE]: role === 'customer',
    [TRANSITION_JOB_DONE_CUSTOMER]: role === 'customer',
    [TRANSITION_JOB_DONE_PRO]: role === 'customer',

    // Invoice branch transitions
    [TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE]: true, // provider marks done, customer may accept directly
    [TRANSITION_JOB_DONE_INVOICE]: role === 'customer',
  };

  return actionNeededMap[transition] || false;
};

/**
 * Check if reviews have started but not completed
 * @param {object} transaction - The transaction object
 * @returns {boolean} - Whether reviews are in progress
 */
export const areReviewsInProgress = transaction => {
  const lastTransition = transaction.attributes.lastTransition;
  const transitions = (transaction.attributes.transitions || []).map(t => t.transition);

  const reviewStartTransitions = [
    TRANSITION_RELEASE_FUNDS,
    TRANSITION_JOB_DONE_RELEASE_FUNDS,
    TRANSITION_JOB_ACCEPTED,
    TRANSITION_ACCEPT_JOB_INVOICE,
    TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  ];

  const firstReviewTransitions = [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_1_BY_PROVIDER];

  const secondReviewTransitions = [
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
  ];

  const hasStartedReviews =
    reviewStartTransitions.includes(lastTransition) ||
    firstReviewTransitions.includes(lastTransition);

  const hasBothReviews = secondReviewTransitions.includes(lastTransition);

  return hasStartedReviews && !hasBothReviews;
};

/**
 * Get the transition that marks work as complete for the given process
 * @param {boolean} usesInvoicePath - Whether this uses the invoice path
 * @returns {array} - Array of transitions that mark work complete
 */
export const getWorkCompleteTransitions = usesInvoicePath => {
  return usesInvoicePath
    ? [TRANSITION_JOB_DONE_PRO, TRANSITION_JOB_DONE_INVOICE, TRANSITION_JOB_DONE_ACCEPT_INVOICE]
    : [TRANSITION_JOB_DONE, TRANSITION_JOB_DONE_CUSTOMER, TRANSITION_JOB_DONE_RELEASE_FUNDS];
};

/**
 * Get the transition that releases funds for the given process
 * @param {boolean} isProProcess - Whether this is a pro process
 * @returns {array} - Array of transitions that release funds
 */
export const getFundsReleasedTransitions = isProProcess => {
  return isProProcess
    ? [TRANSITION_JOB_ACCEPTED]
    : [TRANSITION_RELEASE_FUNDS, TRANSITION_JOB_DONE_RELEASE_FUNDS];
};

/**
 * Check if a transition marks work as complete
 * @param {string} transition - The transition to check
 * @param {boolean} isProProcess - Whether this is a pro process
 * @returns {boolean} - Whether this transition marks work complete
 */
export const isWorkCompleteTransition = (transition, isProProcess) => {
  const workCompleteTransitions = getWorkCompleteTransitions(isProProcess);
  return workCompleteTransitions.includes(transition);
};

/**
 * Check if a transition releases funds
 * @param {string} transition - The transition to check
 * @param {boolean} isProProcess - Whether this is a pro process
 * @returns {boolean} - Whether this transition releases funds
 */
export const isFundsReleasedTransition = (transition, isProProcess) => {
  const fundsReleasedTransitions = getFundsReleasedTransitions(isProProcess);
  return fundsReleasedTransitions.includes(transition);
};
